import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { FilesSelector } from 'components/FilesSelector/FilesSelector';
import { useReadFilesAsBase64 } from 'components/FilesSelector/hooks/useReadFilesAsBase64';
import { HierarchyViewSelect, type IHierarchyViewSelectItem } from 'components/HierarchyViewSelect/HierarchyViewSelect';
import { ModalDialog } from 'components/ModalDialog';
import { UploadStatus } from 'components/UploadStatus';
import type { IErrorHandlingRequestState } from 'features/errorHandling/errorHandlingSlice';
import { RequestStatus } from 'features/request/requestSlice';
import type { BimChapterEnum, BimWorkOrderObjectData, FileInput } from 'gql/graphql';
import type { IListItem } from 'types/Contract';
import { Button, cnMixSpace, IconAlert, Informer, TextField } from 'uikit';
import { ExportCSVReport } from 'utils/createCSVReportFile';
import type { Meta } from 'utils/types';

import type { PayloadAction } from '@reduxjs/toolkit';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { bimWorkOrderChaptersAndSectionsListItems } from '../../WorkOrder/types';

import { radioItems, ActSourceSelect } from './ActSourceSelect';

const styleWidth = { width: 472 };
const classNameM = cnMixSpace({ mB: 'm' });
const accept = ['.xml'];

interface IUploadPriceFilesModal<T> {
    titleModal: string;
    uploadData: T;
    uploadStatus: RequestStatus;
    uploadError: any;
    uploadMakeRequest: (variables: {
        chapter: BimChapterEnum | undefined;
        filesContent: FileInput[];
        workOrderObjectId: number | undefined;
        rationale?: string;
        currentSelectedObject?: IListItem<number>;
        hasTotalsFromXml?: boolean;
    }) => void;
    uploadReset: (value: unknown) => PayloadAction<any, string, Meta, never>;
    uploadAbort: () => void;
    onClose: (isSuccess: boolean) => void;
    selectedObject?: IListItem<number>;
    chapter?: BimChapterEnum;
    textInformer?: string;
    refreshProject?: () => void;
    objectsListItems: IListItem<number>[];
    successButtonTitle: string;
    outerResetHandler?: () => void;
    isConfirmDisabled?: boolean;
    withRationale?: boolean;
    withActFileSource?: boolean;
    successBtnHandler?: () => void;
}

export const BimUploadPriceFilesModal = <TData extends IErrorHandlingRequestState>(
    props: IUploadPriceFilesModal<TData>,
) => {
    const {
        chapter,
        selectedObject,
        onClose,
        textInformer,
        titleModal,
        uploadData,
        uploadStatus,
        uploadError,
        uploadMakeRequest,
        uploadReset,
        uploadAbort,
        refreshProject,
        objectsListItems,
        successButtonTitle,
        outerResetHandler,
        isConfirmDisabled,
        withRationale = false,
        withActFileSource = false,
        successBtnHandler,
    } = props;

    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isModalActive, setIsModalActive] = useState(false);
    const [isOpenWarningDialog, setIsOpenWarningDialog] = useState(false);

    const [rationale, setRationale] = useState('');
    const [selectedSubObject, setSelectedSubObject] = useState<IHierarchyViewSelectItem>();
    const [selectedActFileSource, setSelectedActFileSource] = useState<(typeof radioItems)[0] | undefined>(() =>
        withActFileSource ? radioItems[0] : undefined,
    );
    const [files, setFiles] = useState<File[] | undefined>([]);
    const base64Files = useReadFilesAsBase64(files);

    const chapterTitle = useMemo(
        () => bimWorkOrderChaptersAndSectionsListItems.find((item) => item.id === chapter)?.label,
        [chapter],
    );

    const btnConfirmDisabled = useMemo(
        () => !chapter || isConfirmDisabled || !base64Files?.length || (withRationale && !rationale.length),
        [base64Files?.length, chapter, isConfirmDisabled, rationale.length, withRationale],
    );

    const onRationaleChange = useCallback(({ value }: { value: string | null }) => {
        setRationale(value ?? '');
    }, []);

    const onActFileSourceChange = useCallback(({ value }: { value: (typeof radioItems)[0] }) => {
        setSelectedActFileSource(value);
    }, []);

    const onFilesChange = useCallback((newFiles?: File[]) => {
        setFiles(newFiles);
    }, []);

    const onCloseModal = useCallback(() => {
        uploadReset(undefined);
        setIsModalActive(false);
    }, [uploadReset]);

    const onCloseDialog = useCallback(() => {
        setIsOpenWarningDialog(true);
    }, []);

    const resetData = useCallback(() => {
        uploadReset(undefined);
        uploadAbort();
        outerResetHandler?.();
    }, [uploadReset, uploadAbort, outerResetHandler]);

    const onSuccess = useCallback(() => {
        resetData();
        successBtnHandler?.();
        onClose(true);
    }, [onClose, resetData, successBtnHandler]);

    const onSubmitWarningDialog = useCallback(() => {
        resetData();
        onClose(false);
    }, [onClose, resetData]);

    const onCancelWarningDialog = useCallback(() => {
        setIsOpenWarningDialog(false);
    }, []);

    const onCloseModalError = useCallback(() => {
        setIsSubmitted(false);
    }, []);

    const onCreateReport = () => {
        ExportCSVReport(uploadData?.validation);
    };

    const onConfirm = useCallback(() => {
        setIsSubmitted(true);

        uploadMakeRequest({
            chapter,
            filesContent: base64Files as FileInput[],
            workOrderObjectId:
                (selectedSubObject as unknown as BimWorkOrderObjectData)?.workOrderObjectId ||
                selectedObject?.workOrderObjectId,
            currentSelectedObject: selectedSubObject ?? selectedObject,
            ...(withRationale ? { rationale } : undefined),
            ...(withActFileSource ? { hasTotalsFromXml: selectedActFileSource?.hasTotalsFromXml } : undefined),
        });
    }, [
        base64Files,
        chapter,
        rationale,
        selectedActFileSource?.hasTotalsFromXml,
        selectedObject,
        selectedSubObject,
        uploadMakeRequest,
        withActFileSource,
        withRationale,
    ]);

    useEffect(() => {
        if (uploadStatus === RequestStatus.SUCCESS && uploadData?.result === true && !uploadData?.message) {
            setIsModalActive(true);
            refreshProject?.();
        }
    }, [uploadData?.message, uploadData?.result, uploadStatus, refreshProject]);

    return (
        <ModalDialog
            title={titleModal}
            isOpen
            onClose={onCloseDialog}
            onConfirm={onConfirm}
            btnConfirmLabel="Добавить"
            btnConfirmDisabled={btnConfirmDisabled}
            style={{ maxWidth: 520 }}
            isOnClickOutsideDisable
        >
            <div className={cnMixSpace({ m: 'xl' })} style={{ display: 'flex', flexDirection: 'column' }}>
                {textInformer && (
                    <Informer
                        view="filled"
                        status="warning"
                        label={textInformer}
                        className={classNameM}
                        icon={IconAlert}
                        size="s"
                        style={{ width: 440 }}
                    />
                )}
                <TextField
                    label="Объект"
                    value={selectedObject?.label}
                    type="text"
                    view="default"
                    labelPosition="left"
                    className={classNameM}
                    style={styleWidth}
                    size="s"
                    disabled
                />
                <TextField
                    label="Глава расчета"
                    value={chapterTitle}
                    type="text"
                    view="default"
                    labelPosition="left"
                    className={classNameM}
                    style={styleWidth}
                    size="s"
                    disabled
                />
                <HierarchyViewSelect
                    label="Подобъект"
                    onlyLeafsSelectable
                    sortItemsById
                    selectedItem={selectedSubObject}
                    items={objectsListItems}
                    onChange={setSelectedSubObject}
                    rootId={selectedObject?.id}
                    style={styleWidth}
                    className={classNameM}
                    size="s"
                    labelPosition="left"
                />
                {withRationale && (
                    <TextField
                        label="Обоснование"
                        value={rationale}
                        onChange={onRationaleChange}
                        type="textarea"
                        view="default"
                        labelPosition="left"
                        className={classNameM}
                        style={styleWidth}
                        size="s"
                        required
                        rows={3}
                    />
                )}

                {withActFileSource && (
                    <ActSourceSelect
                        className={classNameM}
                        value={selectedActFileSource}
                        onChange={onActFileSourceChange}
                    />
                )}

                <FilesSelector
                    files={files}
                    accept={accept}
                    isLoading={uploadStatus === RequestStatus.FETCHING}
                    typeName={accept[0]}
                    manualOperationName="выберите вручную"
                    onFilesChange={onFilesChange}
                    multiple
                    isExternalFilesState
                />
            </div>
            <UploadStatus
                isOpened={isModalActive}
                handleCloseModal={onCloseModal}
                statusText="Проверка завершена. Ошибок не обнаружено."
                titleText={titleModal}
                status="success"
                withTitleCloseButton={false}
            >
                <Button label={successButtonTitle} onClick={onSuccess} />
            </UploadStatus>
            <ConfirmationDialog
                textHeader="Отмена действия"
                textConfirmation="Вы уверены, что хотите отменить?  Данные не будут сохранены"
                cancelButtonText="Не отменять"
                confirmButtonText="Отменить"
                isOpened={isOpenWarningDialog}
                onSubmit={onSubmitWarningDialog}
                onClose={onCancelWarningDialog}
            />
            <ErrorHandlingModal
                titleText={titleModal}
                isSubmitted={isSubmitted}
                statusRequest={uploadStatus}
                dataRequest={uploadData}
                errorsRequest={uploadError}
                handleCloseModalError={onCloseModalError}
                handleCreateReport={onCreateReport}
            />
        </ModalDialog>
    );
};

BimUploadPriceFilesModal.displayName = 'BimUploadPriceFilesModal';
